/**
 * Authenticated Socket.IO connections.
 *
 * The API authenticates the WebSocket HANDSHAKE (a WS upgrade never passes through Express
 * middleware, so REST auth alone left the realtime surface open). Every connection must therefore
 * carry the same access token REST calls use — hence one factory rather than three bare `io(...)`
 * calls that would each have to remember.
 *
 * The token is fetched per connection attempt, including reconnects, so a socket that reconnects
 * after the token expired presents a fresh one instead of being refused forever.
 */
import { io, type Socket } from 'socket.io-client';
import { getAccessToken, getActiveProject } from './apiClient';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/**
 * Connect with authentication. Returns immediately with a socket that connects once the token
 * resolves — callers register handlers synchronously, as they did before.
 */
export function connectSocket(opts: { url?: string; path?: string } = {}): Socket {
  const socket = io(opts.url ?? API_BASE, {
    ...(opts.path !== undefined && { path: opts.path }),
    // Defer connecting until the token is attached, so the handshake carries it on the FIRST attempt
    // rather than failing once and retrying.
    autoConnect: false,
  });

  // Re-read the token before every attempt (including reconnects): `auth` may be a function, which
  // socket.io-client calls per attempt.
  socket.auth = (cb: (data: Record<string, unknown>) => void): void => {
    // The project is sent alongside the token, and re-read per attempt for the same reason the token
    // is: a reconnect after a project switch must not resubscribe against the previous one.
    //
    // The server VALIDATES it against the caller's memberships rather than trusting it — this only
    // says which of the caller's projects the socket is for, it cannot grant access to another.
    const project = getActiveProject();
    void getAccessToken()
      .then((token) => cb({
        ...(token !== null ? { token } : {}),
        ...(project !== null ? { project } : {}),
      }))
      // No token (mock-auth) → connect bare and let the API's dev bypass decide. In a deployed
      // environment that handshake is refused, which is the correct outcome.
      .catch(() => cb(project !== null ? { project } : {}));
  };
  socket.connect();
  return socket;
}
